import React, { useState, useEffect } from 'react';
import { Moon, Shield, AlertTriangle } from 'lucide-react';
import { toast } from '../ui/simple-toaster';
import { useModals } from './hooks/useModals';
import { useVehicle } from './hooks/useVehicle';
import { useJourney } from './hooks/useJourney';
import { useDriverData } from './hooks/useDriverData';
import { useVoiceAlerts } from './hooks/useVoiceAlerts';
import { useTimeDisplay } from './hooks/useTimeDisplay';
import { useWorkRestActions } from './hooks/useWorkRestActions';
import { useDriverActions } from './hooks/useDriverActions';
import { useJourneyActions } from './hooks/useJourneyActions';
import { useVehicleHandlers } from './hooks/useVehicleHandlers';
import { api } from '../../utils/api';
import { formatTime } from '../../utils/formatters';
import { SyncIndicatorMinimal } from '../shared/SyncIndicator';
import { useLocalElapsedTime } from '../../hooks/useLocalTimers';
import { useAdaptiveCountdown, formatCountdown } from '../../hooks/useLocalTimers';
import { syncService } from '../../services/syncService';

// Import sub-components
import {
  DriverHeader,
  MenuPanel,
  StatusCard,
  RestStatusCard,
  ActionButtons,
  QuickActionRow
} from './DriverComponents';

import { DriverModalLayer } from './DriverModalLayer';
import ConsentManagementModal from './ConsentManagementModal';
import FleetInvitesModal from './FleetInvitesModal';

const DriverApp = ({ user, onLogout }) => {
  // === MODALS ===
  const modals = useModals();
  const {
    showMenu, setShowMenu, showLogoutConfirm, setShowLogoutConfirm,
    showJourneyModal, setShowJourneyModal, showFinishModal, setShowFinishModal,
    showNightRestConfirm, setShowNightRestConfirm, showVehicleModal, setShowVehicleModal,
    showRadiusSelectionModal, setShowRadiusSelectionModal, showRadiusWarning, setShowRadiusWarning,
    showAboutModal, setShowAboutModal, showExportModal, setShowExportModal,
    showBaseAddressModal, setShowBaseAddressModal, showOdometerConfirm, setShowOdometerConfirm,
    showFuelStopModal, setShowFuelStopModal, showMyVehicleModal, setShowMyVehicleModal,
    showMyDiaryModal, setShowMyDiaryModal, showReportsModal, setShowReportsModal,
    showRestLocationModal, setShowRestLocationModal, showAcknowledgementModal, setShowAcknowledgementModal,
    ackCheckbox, setAckCheckbox, acknowledgementAccepted, setAcknowledgementAccepted,
    showOtherVehicleForm, setShowOtherVehicleForm,
  } = modals;

  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showFleetInvitesModal, setShowFleetInvitesModal] = useState(false);
  const [fleetInviteCount, setFleetInviteCount] = useState(0);
  const [pendingTrailers, setPendingTrailers] = useState([]);

  // === VEHICLE STATE ===
  const {
    vehicles, setVehicles, otherVehicleForm, setOtherVehicleForm,
    vehicleLoading, setVehicleLoading, selectedVehicle, setSelectedVehicle,
    pendingVehicleSelection, setPendingVehicleSelection,
    radiusSelectionLoading, setRadiusSelectionLoading,
  } = useVehicle();

  // === JOURNEY STATE ===
  const {
    journeyDestination, setJourneyDestination, journeyLoading, setJourneyLoading,
    journeyResult, setJourneyResult, addressSuggestions, setAddressSuggestions,
    showSuggestions, setShowSuggestions,
  } = useJourney();

  // === DRIVER DATA ===
  const {
    driverInfo, driverState, currentSegment, setCurrentSegment,
    loading, elapsedTime, setElapsedTime, weeklyDistance, syncStatus,
    loadDriverData,
  } = useDriverData(user, selectedVehicle, setSelectedVehicle);

  // === UI STATE ===
  const [showRadiusAcknowledged, setShowRadiusAcknowledged] = useState(false);
  const [baseAddressInput, setBaseAddressInput] = useState('');
  const [expandedInfoSection, setExpandedInfoSection] = useState(null);

  // Check if user is owner/driver
  const isOwnerDriver = user?.role === 'OWNER_DRIVER' || user?.roles?.includes('OWNER_DRIVER');

  // === REGIME HELPERS ===
  const vehicleInfo = selectedVehicle || driverState?.vehicle;

  const getRegimeFromVehicle = (v) => {
    if (!v) return null;
    if (v.regime) return v.regime.toUpperCase();
    if (v.gvm_kg && v.gvm_kg < 12000) return 'BUSINESS';
    if (v.regime_label?.toLowerCase().includes('business')) return 'BUSINESS';
    return null;
  };

  const vehicleRegime = getRegimeFromVehicle(vehicleInfo);
  const stateRegime = driverState?.regime?.toUpperCase();
  // Backend regime is authoritative (considers diary type + current segment vehicle).
  // Vehicle regime is only used as a preview before state loads.
  const currentRegime = stateRegime || vehicleRegime || 'FRHV';
  const isBusinessRegime = currentRegime === 'BUSINESS';

  // === COMPUTED VALUES ===
  const isWorking = currentSegment?.segment_type === 'WORK';
  const isResting = currentSegment?.segment_type === 'REST';
  const isNightRest = isResting && currentSegment?.source === 'NIGHT_REST';
  const isDayOffSource = isResting && currentSegment?.source === 'DAY_OFF';
  const timeUntilRest = driverState?.time_until_rest_required_sec || 0;
  const complianceStatus = driverState?.compliance_status || 'GREEN';

  const isUrgent = isBusinessRegime
    ? (complianceStatus === 'LIMIT_REACHED')
    : (timeUntilRest < 1800 || complianceStatus === 'BEYOND_LAW');
  const isWarning = isBusinessRegime
    ? (complianceStatus === 'WARNING')
    : (timeUntilRest < 3600 || complianceStatus === 'SHADOW_LAW');
  const oneAmApplies = !isBusinessRegime && driverState?.one_am_constraint?.applies;

  // === REST COUNTDOWN HOOKS (adaptive polling: 5min normal, 60s when ≤30min) ===
  const nightRestRemaining = useAdaptiveCountdown(driverState?.rest_tracking?.night_rest_required_by);
  const longRestRemaining = useAdaptiveCountdown(driverState?.rest_tracking?.long_rest_required_by);
  const nightCountdown = formatCountdown(nightRestRemaining);
  const longCountdown = formatCountdown(longRestRemaining);

  // === HOOKS ===
  const { voiceMuted, handleVoiceMuteToggle } = useVoiceAlerts(driverState, currentSegment, isBusinessRegime, elapsedTime);
  const { useBaseTime, setUseBaseTime, formatDisplayTime, getClockTime, getClockDate } = useTimeDisplay(driverInfo);

  const workRest = useWorkRestActions({
    driverInfo, driverState, currentSegment, vehicleInfo, isResting, isWorking, elapsedTime,
    setCurrentSegment, setElapsedTime, loadDriverData,
    setShowOdometerConfirm, setShowNightRestConfirm, setShowRestLocationModal,
    selectedVehicle, setSelectedVehicle, pendingTrailers, setPendingTrailers,
  });

  const driverActions = useDriverActions({
    driverInfo, loadDriverData,
    setShowFinishModal, setShowExportModal, setShowBaseAddressModal,
  });

  const journeyActions = useJourneyActions({
    driverInfo,
    journeyDestination, setJourneyDestination,
    journeyLoading, setJourneyLoading,
    setJourneyResult,
    setAddressSuggestions, setShowSuggestions,
    setShowJourneyModal, setShowRadiusWarning,
  });

  // === VEHICLE HANDLERS ===
  const vehicleHandlers = useVehicleHandlers({
    driverInfo,
    vehicles,
    setVehicles,
    otherVehicleForm,
    setVehicleLoading,
    setShowOtherVehicleForm,
    setShowVehicleModal,
    setShowRadiusSelectionModal,
    setPendingVehicleSelection,
    pendingVehicleSelection,
    setSelectedVehicle,
    setRadiusSelectionLoading,
    setPendingTrailers,
    loadDriverData,
  });
  const { loadVehicles, handleOtherVehicleSubmit, handleVehicleSelect, handleRadiusSelection, cancelRadiusSelection } = vehicleHandlers;

  // === LOGOUT ===
  const handleLogoutClick = () => {
    if (isWorking) { setShowLogoutConfirm(true); }
    else { onLogout(); }
  };
  const confirmLogout = () => { setShowLogoutConfirm(false); onLogout(); };
  const cancelLogoutAndSwitchToRest = async () => { setShowLogoutConfirm(false); await workRest.doStartRest(); };

  // === EFFECTS ===
  useEffect(() => {
    if (driverInfo && !acknowledgementAccepted) setShowAcknowledgementModal(true);
  }, [driverInfo, acknowledgementAccepted, setShowAcknowledgementModal]);

  // Load fleet invite count
  useEffect(() => {
    const loadFleetInviteCount = async () => {
      try {
        const data = await api('/group-view/fleet-invites/incoming');
        const pending = (data.invites || []).filter(i => i.status === 'PENDING');
        setFleetInviteCount(pending.length);
      } catch {
        // Table may not exist
      }
    };
    if (driverInfo) loadFleetInviteCount();
  }, [driverInfo]);

  // === RENDER ===
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!driverInfo) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 sm:w-16 sm:h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-lg sm:text-xl font-bold text-white mb-2">Driver Profile Not Found</h2>
          <p className="text-slate-400 mb-4 text-sm sm:text-base">Your account is not linked to a driver profile.</p>
          <button onClick={handleLogoutClick} className="px-4 py-2 bg-slate-700 text-white rounded-lg text-sm sm:text-base">Logout</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <DriverHeader
        user={user}
        vehicleInfo={vehicleInfo}
        isBusinessRegime={isBusinessRegime}
        getClockTime={getClockTime}
        getClockDate={getClockDate}
        useBaseTime={useBaseTime}
        onToggleTime={() => setUseBaseTime(!useBaseTime)}
        onShowVehicleModal={() => { setShowVehicleModal(true); loadVehicles(); }}
        onShowMenu={() => setShowMenu(!showMenu)}
        onOpenFleetInvites={() => setShowFleetInvitesModal(true)}
      />

      {/* Menu Panel */}
      <MenuPanel
        showMenu={showMenu}
        onClose={() => setShowMenu(false)}
        expandedInfoSection={expandedInfoSection}
        onToggleInfoSection={setExpandedInfoSection}
        voiceMuted={voiceMuted}
        onToggleVoice={handleVoiceMuteToggle}
        onShowVehicleModal={() => { setShowMenu(false); setShowVehicleModal(true); loadVehicles(); }}
        onShowBaseAddress={() => {
          setShowMenu(false);
          setBaseAddressInput(driverState?.base_address || '');
          setShowBaseAddressModal(true);
        }}
        baseAddress={driverState?.base_address}
        onShowAbout={() => { setShowMenu(false); setShowAboutModal(true); }}
        onShowExport={() => { setShowMenu(false); setShowExportModal(true); }}
        onLogout={handleLogoutClick}
        isOwnerDriver={isOwnerDriver}
        onShowMyVehicle={() => { setShowMenu(false); setShowMyVehicleModal(true); }}
        onShowFuelStop={() => { setShowMenu(false); setShowFuelStopModal(true); }}
        isNonFRHV={isBusinessRegime}
        onShowRecordKeeper={() => { setShowMenu(false); setShowMyDiaryModal(true); }}
        onShowReports={() => { setShowMenu(false); setShowReportsModal(true); }}
        onShowConsent={() => { setShowMenu(false); setShowConsentModal(true); }}
        onShowFleetInvites={() => { setShowMenu(false); setShowFleetInvitesModal(true); }}
        fleetInviteCount={fleetInviteCount}
      />

      {/* Main Content */}
      <main className="p-3 sm:p-4 space-y-3 sm:space-y-4 pb-24 sm:pb-32">
        {/* Status Card */}
        <StatusCard
          isWorking={isWorking}
          isResting={isResting}
          isNightRest={isNightRest}
          isDayOffSource={isDayOffSource}
          elapsedTime={elapsedTime}
          workRestartAt={driverState?.work_restart_at}
          workRestartRemainingSec={driverState?.work_restart_remaining_sec || 0}
          canStartWork={driverState?.can_start_work !== false}
          formatDisplayTime={formatDisplayTime}
        />

        {/* Rest Status Row */}
        {driverState && (
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <RestStatusCard
              isResting={isResting}
              isBusinessRegime={isBusinessRegime}
              driverState={driverState}
              elapsedTime={elapsedTime}
              isUrgent={isUrgent}
              isWarning={isWarning}
              currentSegment={currentSegment}
              complianceStatus={complianceStatus}
              formatAbsoluteTime={(ts) => formatDisplayTime(ts)}
            />

            {/* Rest Credits (FRHV only) */}
            {!isBusinessRegime && (
              <div className="bg-slate-800 rounded-xl p-3 sm:p-4 border border-slate-700">
                <p className="text-slate-400 text-[10px] sm:text-xs">Rest Credits</p>
                <p className="text-lg sm:text-xl font-bold text-white">{driverState?.R_credits_24 ?? 0} / {driverState?.max_credits_day ?? 4}</p>
                <div className="flex gap-1 mt-1.5 sm:mt-2">
                  {[1,2,3,4].map(i => (
                    <div key={i} className={`h-1 sm:h-1.5 flex-1 rounded ${i <= (driverState?.R_credits_24 ?? 0) ? 'bg-emerald-500' : 'bg-slate-700'}`} />
                  ))}
                </div>
              </div>
            )}
            {isBusinessRegime && <div />}
          </div>
        )}

        {/* 1AM Rule Warning (FRHV only) */}
        {!isBusinessRegime && oneAmApplies && driverState?.diary_type === 'FRHV' && (
          <div className="rounded-2xl p-3 sm:p-4 bg-purple-500/20 border border-purple-500/50">
            <div className="flex items-center gap-2 sm:gap-3">
              <Moon className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-purple-400">1AM Night Rest Rule</p>
                <p className="text-white text-xs sm:text-sm truncate">{driverState.one_am_constraint?.message}</p>
              </div>
            </div>
          </div>
        )}

        {/* Business Layer Warning for FRHV drivers */}
        {!isBusinessRegime && driverState?.business_layer?.warning_active && (
          <div className={`rounded-2xl p-3 sm:p-4 ${
            driverState.business_layer.status === 'BIZ_LIMIT'
              ? 'bg-orange-500/20 border border-orange-500/50'
              : 'bg-yellow-500/20 border border-yellow-500/50'
          }`}>
            <div className="flex items-center gap-2 sm:gap-3">
              <AlertTriangle className={`w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 ${
                driverState.business_layer.status === 'BIZ_LIMIT' ? 'text-orange-400' : 'text-yellow-400'
              }`} />
              <div className="min-w-0">
                <p className={`text-xs sm:text-sm font-medium ${
                  driverState.business_layer.status === 'BIZ_LIMIT' ? 'text-orange-400' : 'text-yellow-400'
                }`}>
                  {driverState.business_layer.status === 'BIZ_LIMIT'
                    ? 'Business Work Limit Reached'
                    : 'Approaching Business Work Limit'}
                </p>
                <p className="text-[10px] sm:text-xs text-slate-400">
                  Company policy: rest after {driverState.business_layer.work_limit_display}.
                  {driverState.business_layer.time_until_limit_sec > 0
                    ? ` ${Math.ceil(driverState.business_layer.time_until_limit_sec / 60)}m until limit.`
                    : ' Limit reached — rest now.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Compliance Status Banner */}
        {(isBusinessRegime ? (complianceStatus === 'LIMIT_REACHED' || complianceStatus === 'WARNING') : complianceStatus !== 'GREEN') && (
          <div className={`rounded-2xl p-3 sm:p-4 ${
            isBusinessRegime
              ? (complianceStatus === 'LIMIT_REACHED' ? 'bg-red-500/20 border border-red-500/50' : 'bg-amber-500/20 border border-amber-500/50')
              : (complianceStatus === 'BEYOND_LAW' ? 'bg-red-500/20 border border-red-500/50' : 'bg-amber-500/20 border border-amber-500/50')
          }`}>
            <div className="flex items-center gap-2 sm:gap-3">
              <Shield className={`w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 ${
                isBusinessRegime
                  ? (complianceStatus === 'LIMIT_REACHED' ? 'text-red-400' : 'text-amber-400')
                  : (complianceStatus === 'BEYOND_LAW' ? 'text-red-400' : 'text-amber-400')
              }`} />
              <div className="min-w-0">
                <p className={`text-xs sm:text-sm font-medium ${
                  isBusinessRegime
                    ? (complianceStatus === 'LIMIT_REACHED' ? 'text-red-400' : 'text-amber-400')
                    : (complianceStatus === 'BEYOND_LAW' ? 'text-red-400' : 'text-amber-400')
                }`}>
                  {isBusinessRegime
                    ? (complianceStatus === 'LIMIT_REACHED' ? 'Work Limit Reached' : 'Approaching Work Limit')
                    : (complianceStatus === 'BEYOND_LAW' ? 'LAW Threshold Exceeded' : 'Business Rule Warning')}
                </p>
                <p className="text-[10px] sm:text-xs text-slate-400">
                  {isBusinessRegime
                    ? (complianceStatus === 'LIMIT_REACHED' ? 'Take a rest break now' : 'Warning: approaching limit')
                    : (complianceStatus === 'BEYOND_LAW' ? 'Rest required immediately' : 'Consider taking rest soon')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stats Grid - FRHV Only */}
        {!isBusinessRegime && (
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <div className={`rounded-xl p-3 sm:p-4 border ${
              ((driverState?.W_cont_seconds || 0) + (isWorking ? elapsedTime : 0)) > 17700 ? 'bg-amber-500/10 border-amber-500/50' : 'bg-slate-800 border-slate-700'
            }`}>
              <p className="text-slate-400 text-[10px] sm:text-xs">Continuous Work</p>
              <p className={`text-lg sm:text-xl font-bold ${((driverState?.W_cont_seconds || 0) + (isWorking ? elapsedTime : 0)) > 17700 ? 'text-amber-400' : 'text-white'}`}>
                {formatTime((driverState?.W_cont_seconds || 0) + (isWorking ? elapsedTime : 0))}
              </p>
              <div className="mt-1.5 sm:mt-2 h-1 sm:h-1.5 bg-slate-700 rounded-full overflow-hidden relative">
                <div className="absolute h-full w-0.5 bg-amber-500/50" style={{ left: '93.7%' }} />
                <div
                  className={`h-full rounded-full transition-all ${((driverState?.W_cont_seconds || 0) + (isWorking ? elapsedTime : 0)) > 17700 ? 'bg-amber-500' : 'bg-blue-500'}`}
                  style={{ width: `${Math.min(((driverState?.W_cont_seconds || 0) + (isWorking ? elapsedTime : 0)) / 18900 * 100, 100)}%` }}
                />
              </div>
              <p className="text-[10px] text-slate-500 mt-1">Max 5:15</p>
            </div>

            <div className={`rounded-xl p-3 sm:p-4 border ${
              ((driverState?.W_24_seconds || 0) + (isWorking ? elapsedTime : 0)) > 42000 ? 'bg-amber-500/10 border-amber-500/50' : 'bg-slate-800 border-slate-700'
            }`}>
              <p className="text-slate-400 text-[10px] sm:text-xs">24h Work</p>
              <p className={`text-lg sm:text-xl font-bold ${((driverState?.W_24_seconds || 0) + (isWorking ? elapsedTime : 0)) > 42000 ? 'text-amber-400' : 'text-white'}`}>
                {formatTime((driverState?.W_24_seconds || 0) + (isWorking ? elapsedTime : 0))}
              </p>
              <div className="mt-1.5 sm:mt-2 h-1 sm:h-1.5 bg-slate-700 rounded-full overflow-hidden relative">
                <div className="absolute h-full w-0.5 bg-amber-500/50" style={{ left: '97.2%' }} />
                <div
                  className={`h-full rounded-full transition-all ${((driverState?.W_24_seconds || 0) + (isWorking ? elapsedTime : 0)) > 42000 ? 'bg-amber-500' : 'bg-purple-500'}`}
                  style={{ width: `${Math.min(((driverState?.W_24_seconds || 0) + (isWorking ? elapsedTime : 0)) / 43200 * 100, 100)}%` }}
                />
              </div>
              <p className="text-[10px] text-slate-500 mt-1">Max 12:00</p>
            </div>
          </div>
        )}

        {/* BUSINESS Regime Simple Stats */}
        {isBusinessRegime && (
          <div className="bg-slate-800 rounded-xl p-3 sm:p-4 border border-slate-700">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <p className="text-slate-400 text-xs sm:text-sm">Today&apos;s Work</p>
              <span className="text-base sm:text-lg font-mono text-white">
                {formatTime((driverState?.work_24h_seconds || 0) + (isWorking ? elapsedTime : 0))}
              </span>
            </div>
            <div className="h-1.5 sm:h-2 bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all"
                style={{ width: `${Math.min(((driverState?.work_24h_seconds || 0) + (isWorking ? elapsedTime : 0)) / ((driverState?.work_limit_seconds || 10800) * 4) * 100, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Distribution Gates (FRHV only) */}
        {!isBusinessRegime && driverState?.distribution_gates?.crossings?.length > 0 && (
          <div className="bg-slate-800 rounded-xl p-3 sm:p-4 border border-slate-700">
            <p className="text-slate-400 text-[10px] sm:text-xs mb-2">Distribution Gates</p>
            {driverState.distribution_gates.crossings.map((crossing, idx) => (
              <div key={crossing.gate || `gate-${idx}`} className={`flex items-center justify-between py-1.5 sm:py-2 ${idx > 0 ? 'border-t border-slate-700' : ''}`}>
                <span className="text-white text-xs sm:text-sm">{crossing.gate}</span>
                <span className={`px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs ${
                  crossing.layer === 'LAW' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
                }`}>
                  Need {crossing.required_credits} credits
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Enforcement Mode (FRHV only) */}
        {!isBusinessRegime && (
          <div className="bg-slate-800 rounded-xl p-3 sm:p-4 border border-slate-700">
            <div className="flex items-center justify-between">
              <p className="text-slate-400 text-[10px] sm:text-xs">Enforcement Mode</p>
              <span className={`px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-medium ${
                driverState?.provisional_mode === 'FULL_ENFORCE' ? 'bg-emerald-500/20 text-emerald-400' :
                driverState?.provisional_mode === 'SOFT_ENFORCE' ? 'bg-amber-500/20 text-amber-400' :
                driverState?.provisional_mode === 'WARNING_ONLY' ? 'bg-blue-500/20 text-blue-400' :
                'bg-slate-700 text-slate-400'
              }`}>
                {driverState?.provisional_mode === 'FULL_ENFORCE' ? 'Full Enforce' :
                 driverState?.provisional_mode === 'SOFT_ENFORCE' ? 'Soft Enforce' :
                 driverState?.provisional_mode === 'WARNING_ONLY' ? 'Warning Only' : 'Transition'}
              </span>
            </div>
          </div>
        )}

        {/* Rolling Windows (FRHV only) - Collapsible */}
        {!isBusinessRegime && (
          <details className="bg-slate-800 rounded-xl border border-slate-700">
            <summary className="p-3 sm:p-4 text-slate-400 text-xs sm:text-sm cursor-pointer hover:text-white">
              Extended Rolling Windows (7d / 14d)
            </summary>
            <div className="px-3 sm:px-4 pb-3 sm:pb-4 space-y-2 sm:space-y-3">
              <div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-slate-400">7-Day Work</span>
                  <span className="text-white">{formatTime(driverState?.W_7d_seconds || 0)} / 72:00</span>
                </div>
                <div className="mt-1 h-1 sm:h-1.5 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full"
                    style={{ width: `${Math.min((driverState?.W_7d_seconds || 0) / 259200 * 100, 100)}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-slate-400">14-Day Work</span>
                  <span className="text-white">{formatTime(driverState?.W_14d_seconds || 0)} / 144:00</span>
                </div>
                <div className="mt-1 h-1 sm:h-1.5 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-purple-500 rounded-full"
                    style={{ width: `${Math.min((driverState?.W_14d_seconds || 0) / 518400 * 100, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </details>
        )}

        {/* Night / Long Rest Requirements (FRHV only) */}
        {!isBusinessRegime && driverState?.rest_tracking && (
          <div className="bg-slate-800 rounded-xl p-3 sm:p-4 border border-slate-700 space-y-3" data-testid="rest-requirements-card">
            <p className="text-slate-400 text-[10px] sm:text-xs font-medium uppercase tracking-wider">Rest Requirements</p>
            
            {/* Night Rest Status */}
            <div>
              <div className="flex justify-between items-center text-xs sm:text-sm mb-1">
                <span className="flex items-center gap-1.5">
                  <Moon className="w-3.5 h-3.5 text-indigo-400" />
                  <span className="text-slate-300">Night Rest</span>
                  <span className="text-[10px] text-slate-500">(7h in 24h)</span>
                </span>
                <span className={`font-medium ${
                  driverState.rest_tracking.night_rest_overdue ? 'text-red-400' :
                  (driverState.rest_tracking.hours_since_night_rest != null && driverState.rest_tracking.hours_since_night_rest >= 20) ? 'text-amber-400' :
                  'text-emerald-400'
                }`}>
                  {driverState.rest_tracking.hours_since_night_rest != null
                    ? (driverState.rest_tracking.hours_since_night_rest === 0 && isResting
                      ? 'Ongoing'
                      : `${Math.floor(driverState.rest_tracking.hours_since_night_rest)}h ${Math.round((driverState.rest_tracking.hours_since_night_rest % 1) * 60)}m ago`)
                    : 'No data'}
                </span>
              </div>
              <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    driverState.rest_tracking.night_rest_overdue ? 'bg-red-500' :
                    (driverState.rest_tracking.hours_since_night_rest != null && driverState.rest_tracking.hours_since_night_rest >= 20) ? 'bg-amber-500' :
                    'bg-indigo-500'
                  }`}
                  style={{ width: `${Math.min((driverState.rest_tracking.hours_since_night_rest || 0) / 24 * 100, 100)}%` }}
                />
              </div>
              {driverState.rest_tracking.night_rest_required_by && (
                <div className={`flex items-center justify-between mt-1 px-2 py-1 rounded-lg border text-[10px] sm:text-xs ${
                  nightCountdown.urgency === 'overdue' ? 'text-red-400 bg-red-500/15 border-red-500/30 font-semibold' :
                  nightCountdown.urgency === 'critical' ? 'text-red-400 bg-red-500/10 border-red-500/20 animate-pulse' :
                  nightCountdown.urgency === 'warning' ? 'text-amber-400 bg-amber-500/10 border-amber-500/20' :
                  'text-slate-400 bg-slate-700/40 border-slate-600/30'
                }`} data-testid="countdown-night-rest">
                  <span>{nightCountdown.label}</span>
                  <span className="font-mono tabular-nums tracking-tight">{nightCountdown.display}</span>
                  <span className="text-slate-500 hidden sm:inline">{new Date(driverState.rest_tracking.night_rest_required_by).toLocaleString('en-AU', { hour: '2-digit', minute: '2-digit', weekday: 'short', day: 'numeric', month: 'short' })}</span>
                </div>
              )}
            </div>

            {/* Long Rest Status */}
            <div>
              <div className="flex justify-between items-center text-xs sm:text-sm mb-1">
                <span className="flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5 text-purple-400" />
                  <span className="text-slate-300">Long Rest</span>
                  <span className="text-[10px] text-slate-500">(24h in 7d)</span>
                </span>
                <span className={`font-medium ${
                  driverState.rest_tracking.long_rest_overdue ? 'text-red-400' :
                  (driverState.rest_tracking.hours_since_long_rest != null && driverState.rest_tracking.hours_since_long_rest >= 144) ? 'text-amber-400' :
                  'text-emerald-400'
                }`}>
                  {driverState.rest_tracking.hours_since_long_rest != null
                    ? (driverState.rest_tracking.hours_since_long_rest === 0 && isResting
                      ? 'Ongoing'
                      : `${Math.floor(driverState.rest_tracking.hours_since_long_rest)}h ${Math.round((driverState.rest_tracking.hours_since_long_rest % 1) * 60)}m ago`)
                    : 'No data'}
                </span>
              </div>
              <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    driverState.rest_tracking.long_rest_overdue ? 'bg-red-500' :
                    (driverState.rest_tracking.hours_since_long_rest != null && driverState.rest_tracking.hours_since_long_rest >= 144) ? 'bg-amber-500' :
                    'bg-purple-500'
                  }`}
                  style={{ width: `${Math.min((driverState.rest_tracking.hours_since_long_rest || 0) / 168 * 100, 100)}%` }}
                />
              </div>
              {driverState.rest_tracking.long_rest_required_by && (
                <div className={`flex items-center justify-between mt-1 px-2 py-1 rounded-lg border text-[10px] sm:text-xs ${
                  longCountdown.urgency === 'overdue' ? 'text-red-400 bg-red-500/15 border-red-500/30 font-semibold' :
                  longCountdown.urgency === 'critical' ? 'text-red-400 bg-red-500/10 border-red-500/20 animate-pulse' :
                  longCountdown.urgency === 'warning' ? 'text-amber-400 bg-amber-500/10 border-amber-500/20' :
                  'text-slate-400 bg-slate-700/40 border-slate-600/30'
                }`} data-testid="countdown-long-rest">
                  <span>{longCountdown.label}</span>
                  <span className="font-mono tabular-nums tracking-tight">{longCountdown.display}</span>
                  <span className="text-slate-500 hidden sm:inline">{new Date(driverState.rest_tracking.long_rest_required_by).toLocaleString('en-AU', { hour: '2-digit', minute: '2-digit', weekday: 'short', day: 'numeric', month: 'short' })}</span>
                </div>
              )}
            </div>

            {/* 14-day rest counts */}
            <div className="flex gap-4 pt-1 border-t border-slate-700/50">
              <div className="flex-1 text-center">
                <p className="text-lg font-bold text-white">{driverState.rest_tracking.night_rests_14d ?? '—'}</p>
                <p className="text-[10px] text-slate-500">Night rests (14d)</p>
              </div>
              <div className="flex-1 text-center">
                <p className="text-lg font-bold text-white">{driverState.rest_tracking.long_rests_14d ?? '—'}</p>
                <p className="text-[10px] text-slate-500">Long rests (14d)</p>
              </div>
              <div className="flex-1 text-center" data-testid="consecutive-night-rests">
                <p className={`text-lg font-bold ${driverState.rest_tracking.consecutive_night_rests_met ? 'text-emerald-400' : 'text-red-400'}`}>
                  {driverState.rest_tracking.consecutive_night_rests_met ? 'YES' : 'NO'}
                </p>
                <p className="text-[10px] text-slate-500">Consecutive nights</p>
              </div>
            </div>

            {/* Long/Night Work Alert (Fix 4) */}
            {driverState.long_night_work?.active && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-500/15 border border-orange-500/30" data-testid="long-night-work-alert">
                <AlertTriangle className="w-4 h-4 text-orange-400 flex-shrink-0" />
                <span className="text-xs text-orange-300 font-medium">
                  {driverState.long_night_work.reason === 'work_24h_exceeds_12h'
                    ? 'Long work period — over 12h work in 24h'
                    : 'Night work detected — work between midnight and 6am'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Weekly Distance Card */}
        {weeklyDistance && weeklyDistance.total_distance_km > 0 && (
          <div className="bg-slate-800/60 border border-slate-700/50 rounded-2xl p-3 sm:p-4" data-testid="weekly-distance-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 6v6l4 2"/>
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-slate-400">7-Day Distance</p>
                  <p className="text-lg font-bold text-white">{weeklyDistance.total_distance_km.toLocaleString()} <span className="text-sm font-normal text-slate-400">km</span></p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-400">{weeklyDistance.work_segments_with_odometer} readings</p>
                {weeklyDistance.daily?.length > 0 && (
                  <p className="text-xs text-slate-500">
                    Avg {Math.round(weeklyDistance.total_distance_km / weeklyDistance.daily.length)} km/day
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Quick Action Row */}
        <QuickActionRow
          onShowJourney={() => setShowJourneyModal(true)}
          onShowFinish={() => setShowFinishModal(true)}
        />
      </main>

      {/* Bottom Action Buttons */}
      <ActionButtons
        isWorking={isWorking}
        isResting={isResting}
        onStartWork={workRest.startWork}
        onStartRest={workRest.startRest}
      />

      {/* All Modals */}
      <DriverModalLayer
        // Visibility
        showLogoutConfirm={showLogoutConfirm}
        showNightRestConfirm={showNightRestConfirm}
        showVehicleModal={showVehicleModal}
        showOtherVehicleForm={showOtherVehicleForm}
        showRadiusSelectionModal={showRadiusSelectionModal}
        showJourneyModal={showJourneyModal}
        showRadiusWarning={showRadiusWarning}
        showRadiusAcknowledged={showRadiusAcknowledged}
        showFinishModal={showFinishModal}
        showAboutModal={showAboutModal}
        showExportModal={showExportModal}
        showBaseAddressModal={showBaseAddressModal}
        showAcknowledgementModal={showAcknowledgementModal}
        showOdometerConfirm={showOdometerConfirm}
        showFuelStopModal={showFuelStopModal}
        showMyVehicleModal={showMyVehicleModal}
        showMyDiaryModal={showMyDiaryModal}
        showReportsModal={showReportsModal}
        showRestLocationModal={showRestLocationModal}
        // Close setters
        setShowVehicleModal={setShowVehicleModal}
        setShowOtherVehicleForm={setShowOtherVehicleForm}
        setShowJourneyModal={setShowJourneyModal}
        setShowRadiusWarning={setShowRadiusWarning}
        setShowRadiusAcknowledged={setShowRadiusAcknowledged}
        setShowFinishModal={setShowFinishModal}
        setShowAboutModal={setShowAboutModal}
        setShowExportModal={setShowExportModal}
        setShowBaseAddressModal={setShowBaseAddressModal}
        setShowFuelStopModal={setShowFuelStopModal}
        setShowMyVehicleModal={setShowMyVehicleModal}
        setShowMyDiaryModal={setShowMyDiaryModal}
        setShowReportsModal={setShowReportsModal}
        // Logout
        confirmLogout={confirmLogout}
        cancelLogoutAndSwitchToRest={cancelLogoutAndSwitchToRest}
        // Night rest
        nightRestMessage={workRest.nightRestMessage}
        nightRestConfirmType={workRest.nightRestConfirmType}
        handleNightRestConfirmProceed={workRest.handleNightRestConfirmProceed}
        setShowNightRestConfirm={setShowNightRestConfirm}
        // Vehicle
        vehicles={vehicles}
        vehicleInfo={vehicleInfo}
        vehicleLoading={vehicleLoading}
        handleVehicleSelect={handleVehicleSelect}
        otherVehicleForm={otherVehicleForm}
        setOtherVehicleForm={setOtherVehicleForm}
        handleOtherVehicleSubmit={handleOtherVehicleSubmit}
        // Radius
        pendingVehicleSelection={pendingVehicleSelection}
        handleRadiusSelection={handleRadiusSelection}
        cancelRadiusSelection={cancelRadiusSelection}
        radiusSelectionLoading={radiusSelectionLoading}
        // Journey
        journeyDestination={journeyDestination}
        handleAddressChange={journeyActions.handleAddressChange}
        checkJourneyRadius={journeyActions.checkJourneyRadius}
        journeyLoading={journeyLoading}
        addressSuggestions={addressSuggestions}
        showSuggestions={showSuggestions}
        selectAddress={journeyActions.selectAddress}
        setJourneyDestination={setJourneyDestination}
        journeyResult={journeyResult}
        setJourneyResult={setJourneyResult}
        // Finish day
        handleFinishDay={driverActions.handleFinishDay}
        driverState={driverState}
        // Export
        handleExportPDF={driverActions.handleExportPDF}
        handleSharePDF={driverActions.handleSharePDF}
        exportLoading={driverActions.exportLoading}
        // Base address
        baseAddressInput={baseAddressInput}
        setBaseAddressInput={setBaseAddressInput}
        handleBaseAddressUpdate={driverActions.handleBaseAddressUpdate}
        // Acknowledgement
        ackCheckbox={ackCheckbox}
        setAckCheckbox={setAckCheckbox}
        setAcknowledgementAccepted={setAcknowledgementAccepted}
        setShowAcknowledgementModal={setShowAcknowledgementModal}
        // Odometer
        handleOdometerConfirm={workRest.handleOdometerConfirm}
        odometerLoading={workRest.odometerLoading}
        setPendingWorkStart={workRest.setPendingWorkStart}
        // Fuel stop
        handleFuelStop={workRest.handleFuelStop}
        // My Vehicle
        loadVehicles={loadVehicles}
        // My Diary / Reports
        driverInfo={driverInfo}
        user={user}
        // Rest location
        handleRestLocationConfirm={workRest.handleRestLocationConfirm}
        handleRestLocationClose={workRest.handleRestLocationClose}
      />
      <ConsentManagementModal
        show={showConsentModal}
        onClose={() => setShowConsentModal(false)}
        driverId={driverInfo?.driver_id}
      />
      {showFleetInvitesModal && (
        <FleetInvitesModal onClose={() => { setShowFleetInvitesModal(false); }} />
      )}
    </div>
  );
};

export default DriverApp;
